import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Tooltip } from '@fiverr-private/fit';
import { I18n, I18nProvider } from '@fiverr-private/fiverr_context';

import SafePureComponent from '../shared/SafePureComponent';
import { REVIEWS } from '../../utils/pageSections';
import { validateOverview } from '../../utils/mainComponentsValidator';
import { isFiverrChoice } from '../../utils/fiverrChoice';
import { isRisingTalent } from '../../utils/risingTalent';
import { getCurrentContent } from '../../utils/ugc/getCurrentContent';
import OverviewProfileImage from './ProfileImage';
import Breadcrumbs from './Breadcrumbs';

import './style.scss';

class GigOverview extends Component {

    constructor(props) {
        super(props);

        this.onReviewsClick = this.onReviewsClick.bind(this);
    }

    onReviewsClick() {
        this.props.scrollToSection({ sectionKey: REVIEWS });
    }

    showRisingTalentBadge() {
        const { general, requestContext } = this.context;
        const { params } = requestContext;
        const { isHandpicked } = general;

        return isRisingTalent({ params, isHandpicked });
    }

    showFiverrChoiceBadge() {
        const { requestContext, choiceEligibilities } = this.context;
        const {
            params,
            ab_tests: abTests
        } = requestContext;

        return isFiverrChoice(params, abTests, choiceEligibilities);
    }

    showUserInfo() {
        const { ordersInQueue, ratingsCount } = this.props.overview.gig;
        const showOrdersInQueue = ordersInQueue > 0;
        const showRating = ratingsCount > 0;

        return showRating || showOrdersInQueue || this.showFiverrChoiceBadge() || this.showRisingTalentBadge();
    }

    render() {
        const { overview, useStickyLayout, scrollToSection, studioInfo, gigTitle } = this.props;

        if (!validateOverview({ overview })) {
            return null;
        }

        const { gig, categories, seller } = overview;
        const { ordersInQueue, ratingsCount, rating } = gig;
        const showOrdersInQueue = ordersInQueue > 0;
        const showRating = ratingsCount > 0;
        const showFiverrChoiceBadge = this.showFiverrChoiceBadge();
        const showRisingTalentBadge = !showFiverrChoiceBadge && this.showRisingTalentBadge();
        const showUserInfo = this.showUserInfo();

        return (
            <div className="gig-overview">
                <Breadcrumbs categories={categories}/>
                <h1 className="text-display-3">{gigTitle}</h1>
                <div className="seller-overview">
                    <OverviewProfileImage seller={seller} studioInfo={studioInfo}
                        scrollToSection={scrollToSection}
                        useStickyLayout={useStickyLayout}/>
                    {showUserInfo &&
                        <div className="user-info">
                            {showRating &&
                                <span className="user-info-rating" onClick={this.onReviewsClick}>
                                    <div className="star-rating-s15-wrapper">
                                        <span className={`star-rating-s15 rate-${Math.round(rating * 2)}`}/>
                                    </div>
                                    <span className="total-rating-out-five">{rating.toFixed(1)}</span>
                                    <span className="total-rating">({ratingsCount})</span>
                                </span>
                            }
                            {showOrdersInQueue &&
                                <span className="orders-in-queue">
                                    <I18n k="gig_page_perseus.overview.orders_in_queue" params={{ amount: ordersInQueue }}/>
                                </span>
                            }
                            {showFiverrChoiceBadge &&
                                <Tooltip content={<I18n k="gig_page_perseus.overview.fiverr_choice"/>}
                                    position={Tooltip.POSITIONS.BOTTOM}>
                                    <span className="fiverr-choice"/>
                                </Tooltip>
                            }
                            {showRisingTalentBadge &&
                                <I18nProvider>
                                    {({ i18n }) => (
                                        <Tooltip content={i18n.t('gig_page_perseus.overview.rising_talent.tooltip')}
                                            position={Tooltip.POSITIONS.BOTTOM}>
                                            <span className="rising-talent">
                                                <I18n k="gig_page_perseus.overview.rising_talent.caption"/>
                                            </span>
                                        </Tooltip>
                                    )}
                                </I18nProvider>
                            }
                        </div>
                    }
                </div>
            </div>
        );
    }
}

GigOverview.propTypes = {
    overview: PropTypes.shape({
        gig: PropTypes.shape({
            title: PropTypes.string.isRequired,
            rating: PropTypes.number,
            ratingsCount: PropTypes.number,
            ordersInQueue: PropTypes.number
        }),
        categories: PropTypes.shape({
            category: PropTypes.shape({
                name: PropTypes.string,
                slug: PropTypes.string
            }),
            subCategory: PropTypes.shape({
                name: PropTypes.string,
                slug: PropTypes.string
            }),
            nestedSubCategory: PropTypes.shape({
                name: PropTypes.string,
                slug: PropTypes.string
            })
        }),
        seller: PropTypes.shape({
            id: PropTypes.number.isRequired,
            username: PropTypes.string,
            achievement: PropTypes.number,
            isPro: PropTypes.bool,
            hasProfilePhoto: PropTypes.bool,
            profilePhoto: PropTypes.string
        })
    }),
    studioInfo: PropTypes.object,
    scrollToSection: PropTypes.func,
    useStickyLayout: PropTypes.bool
};

GigOverview.defaultProps = {
    studioInfo: {}
};

GigOverview.contextTypes = {
    general: PropTypes.object,
    requestContext: PropTypes.object,
    choiceEligibilities: PropTypes.array
};

const mapStateToProps = ({ ugc }) => {
    const { gigTitle } = getCurrentContent({ ugc });

    return {
        gigTitle
    };
};

const ConnectedOverView = connect(mapStateToProps)(GigOverview);

export { GigOverview, ConnectedOverView };
export default SafePureComponent(ConnectedOverView);
